import { memo, useEffect, useState } from 'react';
import { Checkbox } from 'antd';
import cn from 'classnames';

import { CompanySkillMatch, ProgAIShortCompany, ProgAIMetadata } from 'shared/generated-models';
import { Icon, Text } from 'shared/components/ui';
import { TableSelectionMode, Td, Tr } from 'features/table';
import { CandidateProfileAvatar } from 'shared/components';

import styles from './index.module.scss';

interface Props {
	onClick: () => void;
	checkbox: {
		checked: boolean;
		onChange: () => void;
		mode: TableSelectionMode;
	};
	candidate: ProgAIShortCompany;
	meta: ProgAIMetadata;
}

const formatWebsite = (website: string) => {
	if (website.includes('http://')) {
		website = website.split('http://')[1];
	}
	if (website.includes('https://')) {
		website = website.split('https://')[1];
	}
	if (website.endsWith('/')) {
		website = website.slice(0, -1);
	}
	if (website.startsWith('www.')) {
		website = website.slice(4, website.length);
	}
	return website;
};

const formatLocation = (city?: string, country?: string) => {
	if (!city && !country) return '';
	if (!country) return city;
	if (!city) return country;
	return `${city}, ${country}`;
};

const companyInitials = (name?: string): string => {
	if (!name) return '';
	const words = name.trim().split(/\s+/);
	const initials = words.map(word => word.charAt(0).toUpperCase()).join('');
	return initials;
};

const extractDomain = (url?: string): string => {
	if (!url) return '';
	try {
		const { protocol, host } = new URL(url);
		return `${protocol}//${host}`;
	} catch (error) {
		console.error('Invalid URL:', url);
		return '';
	}
};

const Tag = ({ tag }: { tag: CompanySkillMatch }) => {
	return (
		<div className={styles.tag}>
			<Text variant="inter/12/semi">{tag.weight}</Text>
			<Text variant="inter/14/regular" className={styles.tagDivider}>
				|
			</Text>
			<Text variant="inter/12/semi" color="dark_green">
				{tag.skill}
			</Text>
		</div>
	);
};

const MAX_SKILLS = 4;

export const CompanyRow = memo(({ onClick, checkbox, candidate, meta }: Props) => {
	const [logo, setLogo] = useState<string | null>(null);
	const [showMore, setShowMore] = useState<boolean>(false);

	useEffect(() => {
		const fetchLogo = async () => {
			try {
				const logoUrl = `https://logo.clearbit.com/${extractDomain(candidate?.website)}`;
				const response = await fetch(logoUrl);
				if (response.ok) {
					setLogo(logoUrl);
				} else {
					setLogo(null);
				}
			} catch (error) {
				console.error('Failed to fetch logo:', error);
				setLogo(null);
			}
		};

		fetchLogo();
	}, [candidate]);

	const company = candidate;

	const openLink = (link: string) => () => {
		window.open(link, '_blank');
	};

	return (
		<>
			<Tr className={styles.profile} onClick={onClick}>
				<div className={styles.border}></div>
				<div className={styles.wrapper}>
					<Td width="48px">
						<Checkbox
							className={cn(styles.profileCheckbox, {
								[styles.profileCheckboxVisible]: checkbox.checked
							})}
							checked={checkbox.checked}
							onChange={checkbox.onChange}
						/>
					</Td>
					<Td className={styles.cell} width="20%">
						<div className={styles.profileNameWrapper}>
							{logo ? (
								<img className={styles.logo} src={logo} alt="logo" />
							) : (
								<CandidateProfileAvatar size={32}>
									{companyInitials(company.domain)}
								</CandidateProfileAvatar>
							)}
							<Text className={styles.name} variant="grotesk/16/bold" component="p">
								{company.company_name}
							</Text>
						</div>
					</Td>
					<Td className={styles.cell} width="20%">
						<div className={styles.links}>
							{company.cleanCompanyLinkedinUrl && (
								<Icon
									icon="linkedin-logo"
									className={styles.linksLinkedin}
									size={16}
									onClick={openLink(
										`https://linkedin.com/company/${company.cleanCompanyLinkedinUrl}`
									)}
								/>
							)}
							{company.cleanCompanyLinkedinUrl && company.website && (
								<div className={styles.linksDivider} />
							)}
							{company.website && (
								<p className={styles.linksWebsite} onClick={openLink(company.website)}>
									{formatWebsite(company.website)}
								</p>
							)}
						</div>
					</Td>
					<Td className={styles.cell} width="20%">
						<Text className={styles.title} variant="inter/14/regular" component="p">
							{company.linkedin_industry}
						</Text>
					</Td>
					<Td className={styles.cell} width="16%">
						<Text className={styles.title} variant="inter/14/regular" color="grey">
							{formatLocation(company.city, company.country)}
						</Text>
					</Td>
					<Td className={styles.cell} width="16%">
						<Text variant="inter/14/regular">
							{company.company_size_min && company.company_size_max
								? `${company.company_size_min} - ${company.company_size_max}`
								: ''}
						</Text>
					</Td>
				</div>
				<div className={styles.skillsWrapper}>
					<div className={styles.skillsHeader}>
						<Icon icon="users" size={16} className={styles.skillsIcon} />
						<Text variant="inter/13/regular" color="lighter_grey">
							Skill Distribution
						</Text>
					</div>
					{meta.found_skills && (
						<div className={styles.skills}>
							{(showMore ? meta.found_skills : meta.found_skills?.slice(0, MAX_SKILLS))?.map(
								(tag, index) => (
									<Tag key={index} tag={tag} />
								)
							)}
							{!showMore && meta.found_skills.length > MAX_SKILLS && (
								<div className={styles.skillsMore} onClick={() => setShowMore(true)}>
									<Text variant="inter/12/medium" color="lighter_grey">
										+{meta.found_skills.length - MAX_SKILLS} More
									</Text>
									<Icon icon="chevron" size={20} className={styles.skillsIcon} />
								</div>
							)}
							{showMore && meta.found_skills.length > MAX_SKILLS && (
								<div className={styles.skillsMore} onClick={() => setShowMore(false)}>
									<Text variant="inter/12/medium" color="lighter_grey">
										Show less
									</Text>
									<Icon
										icon="chevron"
										size={20}
										className={cn(styles.skillsIcon, styles.skillsInvert)}
									/>
								</div>
							)}
						</div>
					)}
				</div>
			</Tr>
		</>
	);
}, arePropsEqual);

function arePropsEqual(oldProps: Props, newProps: Props) {
	return oldProps.checkbox.checked === newProps.checkbox.checked && oldProps.checkbox.mode === newProps.checkbox.mode;
}
